import type { CompanyDashboardLinks } from "@/dashboards/companyDashboardsDomain";
import {
  adminArchive,
  adminArticles,
  adminBeneficialOwners,
  adminCompanyDashboard,
  adminDashboard,
  adminEntityData,
  // adminFinancialInstruments, // re-enable with the Instruments menu link below
  adminFollowUp,
  adminMessaging,
  adminOverview,
  adminOwnership,
  adminPartners,
  adminPartnersTransactions,
  adminPoA,
  adminProjects,
  adminRoles,
  adminSettings,
  adminShareholders,
  adminShareholdersDashboard,
  adminShareholdersList,
  adminShareholdersOptions,
  adminShareholdersShareClasses,
  adminShareholdersShareholderCommitments,
  adminShareholdersShareholderContributions,
  adminShareholderStatement,
  adminShareholdersTransactions,
  boardMemberArchive,
  boardMemberMessaging,
  boardMemberOverview,
  boardMemberOverviewArticles,
  scheduleUrl,
  shareholderArchive,
  shareholderMessaging,
  shareholderOverview,
  shareholderOverviewArticles,
} from "@/utils/navigation";
import { IconName } from "@/components-v2/iconMapping";
import {
  canRenderAdminMeetings,
  canRenderAdminProjects,
  canRenderArchive,
  canRenderMessages,
  isOwnershipRegisterSubscription,
  isRegisterSubscription,
} from "@/payment/ApplicationFeatures";
import { compact, trimEnd } from "lodash";
import { TMessage } from "@/i18n/tt";
import { isCountryCodeNorway } from "@/utils/countries";
import { AccessType } from "@/types/enums/AccessType";
import { PaidSubscription } from "@/common/subscription";
import { CustomRoleForUser } from "@/common/customRole";
import { BetaLabelProps } from "@/components-v2/BetaLabel";
import { TagColor } from "@/components-v2/Tag";
import { isContributionsEnabled } from "@/shareholderRegister/contributions/shareholderContributionsDomain";
import { FeatureToggles, sharesDataWithTRank } from "@/common/featureToggles";
import { OwnershipModel } from "@/companyRegistry/companyRegistryApiClient";

export type HeaderLink = {
  icon: IconName;
  to: string;
  text: TMessage;
  basePath: string;
  exact?: boolean;
  badge?: TMessage;
  badgeColor?: BetaLabelProps["color"];
  tag?: TMessage;
  tagColor?: TagColor;
  subLinks?: SubLink[];
};

export type SubLink = {
  to: string;
  text: TMessage;
  exact?: boolean;
  badge?: TMessage;
  tag?: TMessage;
  badgeColor?: BetaLabelProps["color"];
};

export const adminLinks = ({
  orgNo,
  country,
  subscription,
  userRole,
  followUpCount,
  featureToggles,
  ownershipModel,
  showShareClasses = true,
  companyDashboards = { isSystemDefaultShown: true, dashboards: [] },
}: {
  orgNo: string;
  country?: string;
  subscription?: PaidSubscription;
  userRole?: CustomRoleForUser;
  followUpCount?: number;
  featureToggles: FeatureToggles;
  ownershipModel?: OwnershipModel;
  showShareClasses?: boolean;
  companyDashboards?: CompanyDashboardLinks;
}): HeaderLink[] => {
  const noRole = userRole === undefined || userRole.customRoleId === "all";
  const register = isRegisterSubscription(subscription);
  const ownershipRegister = isOwnershipRegisterSubscription(subscription);
  const partnership = ownershipModel === "partnership_interests";
  const shareholdersSubLinks: SubLink[] = compact([
    (!subscription || subscription.allowShareholderRegisterDashboard) &&
      (noRole || userRole?.allowShareholderRegisterDashboardView) && {
        to: adminShareholdersDashboard(orgNo),
        text: { key: "common.dashboard" },
      },
    (!subscription || subscription.allowShareholders) &&
      (noRole || userRole?.allowShareholdersView || userRole?.allowShareholdersEdit) && {
        to: adminShareholdersList(orgNo),
        text: { key: "header.shareholders" },
      },
    (!subscription || subscription.allowTransactions) &&
      (noRole || userRole?.allowTransactionsView || userRole?.allowTransactionsEdit) && {
        to: adminShareholdersTransactions(orgNo),
        text: { key: "header.transactions" },
      },
    (!subscription || subscription.allowShareClasses) &&
      (noRole || userRole?.allowShareClassView || userRole?.allowShareClassEdit) && {
        to: adminShareholdersShareClasses(orgNo),
        text: { key: "header.stock-classes" },
      },
  ]);
  const ownershipRegistersSubLinks: SubLink[] = compact([
    (!subscription || subscription.allowOwners) &&
      (noRole || userRole?.allowOwnersView || userRole?.allowOwnersEdit) && {
        to: adminShareholdersList(orgNo),
        text: { key: "header.shareholders" },
      },
    (!subscription || subscription.allowOwnersTransactions) &&
      (noRole ||
        userRole?.allowOwnersTransactionsView ||
        userRole?.allowOwnersTransactionsEdit) && {
        to: adminShareholdersTransactions(orgNo),
        text: { key: "header.transactions" },
      },
    showShareClasses &&
      (!subscription || subscription.allowOwnersShareClasses) &&
      (noRole ||
        userRole?.allowOwnersShareClassesView ||
        userRole?.allowOwnersShareClassesEdit) && {
        to: adminShareholdersShareClasses(orgNo),
        text: { key: "header.stock-classes" },
      },
  ]);
  if (isContributionsEnabled(orgNo)) {
    shareholdersSubLinks.push({
      to: adminShareholdersShareholderContributions(orgNo),
      text: { key: "header.shareholder-contributions" },
    });
  }

  if (isCountryCodeNorway(country) && featureToggles.committedCapital) {
    if (noRole || userRole?.allowCommitmentsEdit || userRole?.allowCommitmentsView) {
      shareholdersSubLinks.push({
        to: adminShareholdersShareholderCommitments(orgNo),
        text: { key: "header.shareholder-commitments" },
      });
    }
  }
  if (
    !country ||
    (isCountryCodeNorway(country) &&
      (noRole ||
        userRole?.allowShareholderRegisterStatementView ||
        userRole?.allowShareholderRegisterStatementEdit))
  ) {
    shareholdersSubLinks.push({
      to: adminShareholderStatement(orgNo),
      text: { key: "header.altinn" },
    });
  }
  if (
    isCountryCodeNorway(country) &&
    sharesDataWithTRank(featureToggles) &&
    (noRole || userRole?.allowBeneficialOwnersView || userRole?.allowBeneficialOwnersEdit)
  ) {
    shareholdersSubLinks.push({
      to: adminBeneficialOwners(orgNo),
      text: { key: "header.beneficial-owners" },
    });
  }
  if (
    featureToggles.financialInstrumentOptions &&
    (noRole || userRole?.allowFinancialInstrumentsView || userRole?.allowFinancialInstrumentsEdit)
  ) {
    shareholdersSubLinks.push({
      to: adminShareholdersOptions(orgNo),
      text: { key: "header.financial-instruments" },
    });
  }
  const adminProjectsLink: HeaderLink = {
    icon: "sparkle",
    basePath: adminProjects(orgNo),
    to: adminProjects(orgNo),
    text: { key: "header.dbot" },
  };
  const adminScheduleLink: HeaderLink = {
    icon: "calendar",
    basePath: scheduleUrl({ accessType: AccessType.ADMIN, orgNo }),
    to: scheduleUrl({ accessType: AccessType.ADMIN, orgNo }),
    text: { key: "header.schedule" },
  };

  const archiveLink: HeaderLink = {
    icon: "archive",
    basePath: adminArchive({ orgNo }),
    to: adminArchive({ orgNo }),
    text: { key: "header.archive" },
  };
  const messagesLink: HeaderLink = {
    icon: "envelope",
    basePath: adminMessaging(orgNo),
    to: adminMessaging(orgNo),
    text: { key: "header.messaging" },
  };
  const ownershipSubLink: SubLink | undefined =
    (!subscription || subscription.allowOwnership) &&
    sharesDataWithTRank(featureToggles) &&
    (noRole || userRole?.allowOwnershipView)
      ? { to: adminOwnership(orgNo), text: { key: "header.ownership" } }
      : undefined;
  const dashboardSubLinks: SubLink[] = compact([
    !register &&
      companyDashboards.isSystemDefaultShown &&
      (!subscription || subscription.allowDashboard) &&
      (noRole || userRole.allowDashboardView) && {
        to: adminDashboard(orgNo),
        text: { key: "common.dashboard" },
        exact: true,
      },
    ...companyDashboards.dashboards.map((dashboard) => ({
      to: adminCompanyDashboard(orgNo, dashboard.id),
      text: { translated: dashboard.title },
      exact: true,
    })),
  ]);
  const companySubLinks: SubLink[] = compact([
    register && ownershipSubLink,
    !register &&
      noRole && {
        to: adminFollowUp(orgNo),
        text: { key: "header.follow-up" },
        tag: followUpCount ? { translated: String(followUpCount) } : undefined,
      },
    (!subscription || subscription.allowEntityData) &&
      (noRole || userRole?.allowEntityDataView || userRole?.allowEntityDataEdit) &&
      featureToggles.entityData && {
        to: adminEntityData(orgNo),
        text: { key: "header.entity-data" },
      },
    !partnership &&
      (!subscription || subscription.allowArticles) &&
      (noRole || userRole?.allowArticlesEdit || userRole?.allowArticlesView) && {
        to: adminArticles(orgNo),
        text: { key: "header.articles-of-association" },
      },
    !partnership &&
      (!subscription || subscription.allowPoAs) &&
      (noRole || userRole?.allowPoAsEdit || userRole?.allowPoAsView) && {
        to: adminPoA(orgNo),
        text: { key: "header.poa" },
      },
    !register && ownershipSubLink,
    (!subscription || subscription.allowCompanyRoles) &&
      (noRole || userRole?.allowCompanyRolesEdit || userRole?.allowCompanyRolesView) && {
        to: adminRoles(orgNo),
        text: { key: "companyRoles.header" },
      },
    (!subscription || subscription.allowCompanySettings) &&
      (noRole || userRole?.allowCompanySettingsView || userRole?.allowCompanySettingsEdit) && {
        to: adminSettings(orgNo),
        text: { key: "settings" },
      },
  ]);
  const overviewLink: HeaderLink = {
    icon: "buildings",
    to: adminOverview(orgNo),
    text: { key: "header.company" },
    basePath: adminOverview(orgNo),
    exact: true,
    subLinks: [...dashboardSubLinks, ...companySubLinks],
  };
  const shareholderRegisterLink: HeaderLink = {
    icon: "notebook",
    basePath: adminShareholders(orgNo),
    to: adminShareholdersDashboard(orgNo),
    text: { key: "header.shareholderBook" },
    subLinks: shareholdersSubLinks,
  };
  const ownershipRegisterLink: HeaderLink = {
    icon: "notebook",
    basePath: adminShareholders(orgNo),
    to: adminShareholdersList(orgNo),
    text: { key: "header.investments" },
    tag: { key: "header.simplified" },
    tagColor: "neutral",
    subLinks: ownershipRegistersSubLinks,
  };
  const partnershipRegisterLink: HeaderLink = {
    icon: "handshake",
    basePath: adminPartners(orgNo),
    to: adminPartners(orgNo),
    text: { key: "partnership-register.partners" },
    subLinks: compact([
      (!subscription || subscription.allowOwners) &&
        (noRole || userRole?.allowOwnersView || userRole?.allowOwnersEdit) && {
          to: adminPartners(orgNo),
          text: { key: "partnership-register.partners" },
          exact: true,
        },
      (!subscription || subscription.allowOwnersTransactions) &&
        (noRole ||
          userRole?.allowOwnersTransactionsView ||
          userRole?.allowOwnersTransactionsEdit) && {
          to: adminPartnersTransactions(orgNo),
          text: { key: "header.transactions" },
        },
    ]),
  };
  // Instruments menu is temporarily disabled until there are multiple sub modules.
  // Re-enable this link (and its entry in the return array below) at that point.
  // const financialInstrumentsLink: HeaderLink = {
  //   icon: "chart-donut",
  //   basePath: adminFinancialInstruments(orgNo),
  //   to: adminFinancialInstruments(orgNo),
  //   text: { key: "header.financial-instruments" },
  //   subLinks: compact([
  //     (noRole ||
  //       userRole?.allowFinancialInstrumentsView ||
  //       userRole?.allowFinancialInstrumentsEdit) && {
  //       to: adminShareholdersOptions(orgNo),
  //       text: { key: "header.options" },
  //     },
  //   ]),
  // };
  return compact([
    (noRole || userRole?.allowOverview) && overviewLink,
    partnership && (noRole || userRole.allowOwnershipRegister) && partnershipRegisterLink,
    !partnership &&
      !register &&
      (noRole || userRole.allowShareholderRegister) &&
      shareholderRegisterLink,
    !partnership &&
      ownershipRegister &&
      (noRole || userRole.allowOwnershipRegister) &&
      ownershipRegisterLink,
    // (!isProductionEnvironment() || featureToggles.financialInstrumentOptions) &&
    //   (noRole ||
    //     userRole?.allowFinancialInstrumentsView ||
    //     userRole?.allowFinancialInstrumentsEdit) &&
    //   financialInstrumentsLink,
    !partnership &&
      (noRole || userRole.allowDbot) &&
      canRenderAdminProjects(subscription) &&
      adminProjectsLink,
    !partnership &&
      (noRole || userRole?.allowDbot) &&
      canRenderAdminMeetings(subscription) &&
      adminScheduleLink,
    (noRole || userRole.allowArchive) && canRenderArchive(subscription) && archiveLink,
    !partnership &&
      (noRole || userRole.allowMessaging) &&
      canRenderMessages(subscription) &&
      messagesLink,
  ]);
};

export const optionHolderLinks = (orgNumber: string, shareholderId: number): HeaderLink[] => [
  {
    icon: "buildings",
    to: shareholderOverview(orgNumber, shareholderId, "options"),
    text: { key: "header.company" },
    basePath: shareholderOverview(orgNumber, shareholderId),
    exact: true,
    subLinks: [
      {
        to: shareholderOverview(orgNumber, shareholderId, "options"),
        text: { key: "header.options" },
        exact: true,
      },
      {
        to: shareholderOverview(orgNumber, shareholderId, "details"),
        text: { key: "shareholders.details" },
        exact: true,
      },
      {
        to: shareholderOverviewArticles(orgNumber, shareholderId),
        text: { key: "header.articles-of-association" },
        exact: true,
      },
    ],
  },
];

export const shareholderLinks = (
  orgNumber: string,
  shareholderId: number,
  featureToggles: FeatureToggles,
  hasOptions?: boolean,
): HeaderLink[] => [
  {
    icon: "buildings",
    to: shareholderOverview(orgNumber, shareholderId),
    text: { key: "header.company" },
    basePath: shareholderOverview(orgNumber, shareholderId),
    exact: true,
    subLinks: compact([
      {
        to: shareholderOverview(orgNumber, shareholderId, "dashboard"),
        text: { key: "common.dashboard" },
        exact: true,
      },
      {
        to: shareholderOverview(orgNumber, shareholderId, "overview"),
        text: { key: "shareholder-details.shareholding" },
        exact: true,
      },
      {
        to: shareholderOverview(orgNumber, shareholderId, "transactions"),
        text: { key: "shareholder-details.transactions" },
        exact: true,
      },
      {
        to: shareholderOverview(orgNumber, shareholderId, "details"),
        text: { key: "shareholders.details" },
        exact: true,
      },
      featureToggles.committedCapital && {
        to: shareholderOverview(orgNumber, shareholderId, "commitments"),
        text: { key: "shareholder-commitments.commitments.heading" },
        exact: true,
      },
      isContributionsEnabled(orgNumber) && {
        to: shareholderOverview(orgNumber, shareholderId, "contributions"),
        text: { key: "shareholder-contributions.contributions.heading" },
        exact: true,
      },
      hasOptions && {
        to: shareholderOverview(orgNumber, shareholderId, "options"),
        text: { key: "header.options" },
        exact: true,
      },
      {
        to: shareholderOverview(orgNumber, shareholderId, "additional-representatives"),
        text: { key: "additional-representatives" },
        exact: true,
      },
      {
        to: shareholderOverviewArticles(orgNumber, shareholderId),
        text: { key: "header.articles-of-association" },
        exact: true,
      },
    ]),
  },
  {
    icon: "calendar",
    to: scheduleUrl({ accessType: AccessType.SHAREHOLDER, orgNo: orgNumber, shareholderId }),
    text: { key: "header.meetings" },
    basePath: scheduleUrl({ accessType: AccessType.SHAREHOLDER, orgNo: orgNumber, shareholderId }),
  },
  {
    icon: "archive",
    to: shareholderArchive(orgNumber, shareholderId),
    text: { key: "header.shareholderview.archive" },
    basePath: shareholderArchive(orgNumber, shareholderId),
  },
  {
    icon: "envelope",
    to: shareholderMessaging(orgNumber, shareholderId),
    text: { key: "header.recipient.messaging" },
    basePath: shareholderMessaging(orgNumber, shareholderId),
  },
];

export const boardMemberLinks = (orgNumber: string): HeaderLink[] => {
  return [
    {
      icon: "buildings",
      to: boardMemberOverview(orgNumber),
      text: { key: "header.company" },
      basePath: boardMemberOverview(orgNumber),
      exact: true,
      subLinks: [
        {
          to: boardMemberOverview(orgNumber),
          text: { key: "common.dashboard" },
          exact: true,
        },
        {
          to: boardMemberOverviewArticles(orgNumber),
          text: { key: "header.articles-of-association" },
          exact: true,
        },
      ],
    },
    {
      icon: "calendar",
      basePath: scheduleUrl({ accessType: AccessType.BOARD_MEMBER, orgNo: orgNumber }),
      to: scheduleUrl({ accessType: AccessType.BOARD_MEMBER, orgNo: orgNumber }),
      text: { key: "header.schedule" },
    },
    {
      icon: "envelope",
      to: boardMemberMessaging(orgNumber),
      text: { key: "header.recipient.messaging" },
      basePath: boardMemberMessaging(orgNumber),
    },
    {
      icon: "archive",
      to: boardMemberArchive({ orgNo: orgNumber }),
      text: { key: "header.archive" },
      basePath: boardMemberArchive({ orgNo: orgNumber }),
    },
  ];
};

export const findActiveTopMenuItem = (
  links: HeaderLink[],
  locationPathName: string,
): HeaderLink | undefined => {
  const match = links.find(
    (m) =>
      (m.basePath && m.basePath === locationPathName) ||
      m.subLinks?.some(
        (s) => trimEnd(s.to, "/") === trimEnd(locationPathName, "/") && s.exact === true,
      ),
  );
  if (match) {
    return match;
  } else {
    const matches = links.filter(
      (m) =>
        (m.basePath === locationPathName && m.exact === true) ||
        (!m.exact && locationPathName.startsWith(m.basePath)) ||
        m.subLinks?.some(({ to, exact }) => locationPathName.startsWith(to) && !exact),
    );
    return matches.reduce<HeaderLink | undefined>(
      (best, m) => (!best || m.basePath.length > best.basePath.length ? m : best),
      undefined,
    );
  }
};
